import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { CreditCard, DollarSign, TrendingUp, AlertCircle, Search, Filter, Download, Eye, RefreshCcw } from 'lucide-react';

const AdminPayments: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState<number | null>(null);

  // Mock data - replace with actual API calls
  const payments = [
    {
      id: 1,
      user: 'Jean Dupont',
      userEmail: 'jean.dupont@email.com',
      course: 'Expert gestion GPS',
      amount: 299,
      currency: 'EUR',
      status: 'succeeded',
      paymentMethod: 'card',
      stripePaymentId: 'pi_1234567890',
      createdAt: '2024-01-15T16:30:00Z',
      completedAt: '2024-01-15T16:30:05Z',
      cardLast4: '4242',
      cardBrand: 'visa'
    },
    {
      id: 2,
      user: 'Marie Martin',
      userEmail: 'marie.martin@email.com',
      course: 'Expert charge d\'entraînement',
      amount: 349,
      currency: 'EUR',
      status: 'succeeded',
      paymentMethod: 'card',
      stripePaymentId: 'pi_1234567891',
      createdAt: '2024-01-15T14:20:00Z',
      completedAt: '2024-01-15T14:20:08Z',
      cardLast4: '5555',
      cardBrand: 'mastercard'
    },
    {
      id: 3,
      user: 'Pierre Durand',
      userEmail: 'pierre.durand@email.com',
      course: 'Expert réathlétisation',
      amount: 389,
      currency: 'EUR',
      status: 'failed',
      paymentMethod: 'card',
      stripePaymentId: 'pi_1234567892',
      createdAt: '2024-01-15T12:45:00Z',
      completedAt: null,
      cardLast4: '1234',
      cardBrand: 'visa',
      errorMessage: 'Carte insuffisamment provisionnée'
    },
    {
      id: 4,
      user: 'Sophie Bernard',
      userEmail: 'sophie.bernard@email.com',
      course: 'Expert gestion GPS',
      amount: 299,
      currency: 'EUR',
      status: 'pending',
      paymentMethod: 'card',
      stripePaymentId: 'pi_1234567893',
      createdAt: '2024-01-15T11:15:00Z',
      completedAt: null,
      cardLast4: '9876',
      cardBrand: 'visa'
    },
    {
      id: 5,
      user: 'Lucas Petit',
      userEmail: 'lucas.petit@email.com',
      course: 'Expert charge d\'entraînement',
      amount: 349,
      currency: 'EUR',
      status: 'refunded',
      paymentMethod: 'card',
      stripePaymentId: 'pi_1234567894',
      createdAt: '2024-01-14T18:30:00Z',
      completedAt: '2024-01-14T18:30:12Z',
      refundedAt: '2024-01-15T09:20:00Z',
      cardLast4: '8888',
      cardBrand: 'amex',
      refundAmount: 349,
      refundReason: 'Demande client'
    }
  ];

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.course.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.stripePaymentId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    
    let matchesPeriod = true;
    if (filterPeriod !== 'all') {
      const paymentDate = new Date(payment.createdAt);
      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - paymentDate.getTime()) / (1000 * 3600 * 24));
      
      switch (filterPeriod) {
        case 'today':
          matchesPeriod = daysDiff === 0;
          break;
        case 'week':
          matchesPeriod = daysDiff <= 7;
          break;
        case 'month':
          matchesPeriod = daysDiff <= 30;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesPeriod;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'pending': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'refunded': return 'bg-gray-100 text-gray-800';
      case 'canceled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'succeeded': return 'Réussi';
      case 'pending': return 'En attente';
      case 'failed': return 'Échoué';
      case 'refunded': return 'Remboursé';
      case 'canceled': return 'Annulé';
      default: return status;
    }
  };

  const getCardBrandIcon = (brand: string) => {
    switch (brand) {
      case 'visa': return '💳';
      case 'mastercard': return '💳';
      case 'amex': return '💳';
      default: return '💳';
    }
  };

  // Calculate statistics
  const stats = {
    totalRevenue: payments.filter(p => p.status === 'succeeded').reduce((sum, p) => sum + p.amount, 0),
    totalTransactions: payments.length,
    successfulTransactions: payments.filter(p => p.status === 'succeeded').length,
    failedTransactions: payments.filter(p => p.status === 'failed').length,
    pendingTransactions: payments.filter(p => p.status === 'pending').length,
    refundedAmount: payments.filter(p => p.status === 'refunded').reduce((sum, p) => sum + (p.refundAmount || 0), 0),
    averageTransactionValue: payments.filter(p => p.status === 'succeeded').reduce((sum, p) => sum + p.amount, 0) / payments.filter(p => p.status === 'succeeded').length,
    successRate: (payments.filter(p => p.status === 'succeeded').length / payments.length) * 100
  };

  const handleRefund = (paymentId: number) => {
    if (confirm('Êtes-vous sûr de vouloir rembourser ce paiement ?')) {
      alert(`Remboursement du paiement ${paymentId} initié ! (Fonctionnalité à implémenter)`);
    }
  };

  const exportPayments = () => {
    alert('Export des paiements en cours... (Fonctionnalité à implémenter)');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Paiements</h1>
          <button
            onClick={exportPayments}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </button>
        </div>

        {/* Payment Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <DollarSign className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRevenue.toLocaleString()}€</p>
                <p className="text-sm text-[#FFB639]">+12% ce mois</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <CreditCard className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTransactions}</p>
                <p className="text-sm text-[#FFB639]">{stats.successfulTransactions} réussies</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <TrendingUp className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Taux de réussite</p>
                <p className="text-2xl font-bold text-gray-900">{Math.round(stats.successRate)}%</p>
                <p className="text-sm text-[#FFB639]">Très bon</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-[#FFB639]/15 rounded-lg">
                <AlertCircle className="h-6 w-6 text-[#FFB639]" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendingTransactions}</p>
                <p className="text-sm text-[#FFB639]">À surveiller</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par utilisateur, email, cours ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
            >
              <option value="all">Tous les statuts</option>
              <option value="succeeded">Réussis</option>
              <option value="pending">En attente</option>
              <option value="failed">Échoués</option>
              <option value="refunded">Remboursés</option>
            </select>

            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
            >
              <option value="all">Toutes les périodes</option>
              <option value="today">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
            </select>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {payment.stripePaymentId}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        {getCardBrandIcon(payment.cardBrand)}
                        <span className="ml-1">•••• {payment.cardLast4}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{payment.user}</div>
                      <div className="text-sm text-gray-500">{payment.userEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.course}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {payment.amount}€
                      </div>
                      {payment.status === 'refunded' && payment.refundAmount && (
                        <div className="text-sm text-red-600">
                          -{payment.refundAmount}€ remboursé
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                        {getStatusLabel(payment.status)}
                      </span>
                      {payment.status === 'failed' && payment.errorMessage && (
                        <div className="text-xs text-red-600 mt-1">
                          {payment.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{new Date(payment.createdAt).toLocaleDateString('fr-FR')}</div>
                      <div>{new Date(payment.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => setSelectedPayment(payment.id)}
                        className="text-[#FFB639] hover:text-black"
                      >
                        Voir
                      </button>
                      {payment.status === 'succeeded' && (
                        <button
                          onClick={() => handleRefund(payment.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Rembourser
                        </button>
                      )}
                      {payment.status === 'failed' && (
                        <button
                          onClick={() => alert('Retry payment (À implémenter)')}
                          className="text-[#FFB639] hover:text-black"
                        >
                          Réessayer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredPayments.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <CreditCard className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun paiement trouvé</h3>
              <p className="text-gray-500">
                {searchTerm || filterStatus !== 'all' || filterPeriod !== 'all'
                  ? 'Aucun paiement ne correspond aux critères de recherche'
                  : 'Aucun paiement enregistré'
                }
              </p>
            </div>
          )}
        </div>

        {/* Payment Detail Modal */}
        {selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {(() => {
                const payment = payments.find(p => p.id === selectedPayment);
                if (!payment) return null;

                return (
                  <>
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            Détails du paiement
                          </h2>
                          <p className="text-gray-600">{payment.stripePaymentId}</p>
                        </div>
                        <button
                          onClick={() => setSelectedPayment(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations client</h3>
                          <div className="space-y-3">
                            <div>
                              <span className="text-gray-600">Nom:</span>
                              <span className="ml-2 font-medium">{payment.user}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Email:</span>
                              <span className="ml-2 font-medium">{payment.userEmail}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Cours:</span>
                              <span className="ml-2 font-medium">{payment.course}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails du paiement</h3>
                          <div className="space-y-3">
                            <div>
                              <span className="text-gray-600">Montant:</span>
                              <span className="ml-2 font-medium text-lg">{payment.amount}€</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Devise:</span>
                              <span className="ml-2 font-medium">{payment.currency}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Statut:</span>
                              <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                                {getStatusLabel(payment.status)}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Méthode:</span>
                              <span className="ml-2 font-medium flex items-center">
                                {getCardBrandIcon(payment.cardBrand)}
                                <span className="ml-1">•••• {payment.cardLast4}</span>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Chronologie</h3>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-3 h-3 bg-[#FFB639]/100 rounded-full mr-3"></div>
                            <div>
                              <span className="font-medium">Paiement initié</span>
                              <span className="text-gray-500 ml-2">
                                {new Date(payment.createdAt).toLocaleString('fr-FR')}
                              </span>
                            </div>
                          </div>
                          {payment.completedAt && (
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${
                                payment.status === 'succeeded' ? 'bg-[#FFB639]' : 'bg-red-500'
                              }`}></div>
                              <div>
                                <span className="font-medium">
                                  {payment.status === 'succeeded' ? 'Paiement confirmé' : 'Paiement échoué'}
                                </span>
                                <span className="text-gray-500 ml-2">
                                  {new Date(payment.completedAt).toLocaleString('fr-FR')}
                                </span>
                              </div>
                            </div>
                          )}
                          {payment.refundedAt && (
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                              <div>
                                <span className="font-medium">Remboursé ({payment.refundAmount}€)</span>
                                <span className="text-gray-500 ml-2">
                                  {new Date(payment.refundedAt).toLocaleString('fr-FR')}
                                </span>
                                {payment.refundReason && (
                                  <div className="text-sm text-gray-600">
                                    Raison: {payment.refundReason}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {payment.errorMessage && (
                        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <h4 className="font-medium text-red-800 mb-2">Erreur de paiement</h4>
                          <p className="text-red-700">{payment.errorMessage}</p>
                        </div>
                      )}
                    </div>

                    <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                      {payment.status === 'succeeded' && (
                        <button
                          onClick={() => handleRefund(payment.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          Rembourser
                        </button>
                      )}
                      <button
                        onClick={() => alert('Contacter le client (À implémenter)')}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Contacter le client
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPayments;